import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import {
  GitCompare, Plus, X, Loader, TrendingUp,
  DollarSign, Percent, Award, AlertCircle
} from 'lucide-react';

const ETFComparison = () => {
  const [etfSymbols, setEtfSymbols] = useState(['']);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // ✅ POPULAR ETFs FOR AUTOCOMPLETE
  const popularETFs = [
    { symbol: 'SPY', name: 'SPDR S&P 500 ETF Trust' },
    { symbol: 'QQQ', name: 'Invesco QQQ Trust' },
    { symbol: 'VOO', name: 'Vanguard S&P 500 ETF' },
    { symbol: 'IVV', name: 'iShares Core S&P 500 ETF' },
    { symbol: 'VTI', name: 'Vanguard Total Stock Market ETF' },
    { symbol: 'AGG', name: 'iShares Core US Aggregate Bond ETF' },
    { symbol: 'BND', name: 'Vanguard Total Bond Market ETF' },
    { symbol: 'VEA', name: 'Vanguard FTSE Developed Markets ETF' },
    { symbol: 'IEFA', name: 'iShares Core MSCI EAFE ETF' },
    { symbol: 'VWO', name: 'Vanguard FTSE Emerging Markets ETF' },
    { symbol: 'EEM', name: 'iShares MSCI Emerging Markets ETF' },
    { symbol: 'VNQ', name: 'Vanguard Real Estate ETF' },
    { symbol: 'GLD', name: 'SPDR Gold Trust' },
    { symbol: 'SLV', name: 'iShares Silver Trust' },
    { symbol: 'TLT', name: 'iShares 20+ Year Treasury Bond ETF' },
    { symbol: 'IWM', name: 'iShares Russell 2000 ETF' },
    { symbol: 'XLF', name: 'Financial Select Sector SPDR Fund' },
    { symbol: 'XLE', name: 'Energy Select Sector SPDR Fund' },
    { symbol: 'XLK', name: 'Technology Select Sector SPDR Fund' },
    { symbol: 'XLV', name: 'Health Care Select Sector SPDR Fund' }
  ];

  const addETFInput = () => {
    if (etfSymbols.length < 5) {
      setEtfSymbols([...etfSymbols, '']);
    }
  };

  const removeETFInput = (index) => {
    const newSymbols = etfSymbols.filter((_, i) => i !== index);
    setEtfSymbols(newSymbols);
  };

  const updateETFSymbol = (index, value) => {
    const newSymbols = [...etfSymbols];
    newSymbols[index] = value.toUpperCase();
    setEtfSymbols(newSymbols);

    // ✅ SHOW SUGGESTIONS
    if (value.trim().length > 0) {
      const filtered = popularETFs.filter(etf =>
        etf.symbol.toLowerCase().includes(value.toLowerCase()) ||
        etf.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setActiveSuggestionIndex(index);
    } else {
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  const selectSuggestion = (index, etf) => {
    const newSymbols = [...etfSymbols];
    newSymbols[index] = etf.symbol;
    setEtfSymbols(newSymbols);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
  };

  const compareETFs = async () => {
    const validSymbols = etfSymbols.filter(s => s.trim() !== '');
    
    if (validSymbols.length < 2) {
      setError('Please enter at least 2 ETF symbols to compare');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/etf/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols: validSymbols })
      });

      const data = await response.json();

      if (data.success) {
        setCompareData(data);
      } else {
        setError(data.error || 'Failed to compare ETFs');
      }
    } catch (err) {
      console.error('Error comparing ETFs:', err);
      setError('Failed to fetch comparison data');
    } finally {
      setLoading(false);
    }
  };

  const CHART_COLORS = ['#60a5fa', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  return (
    <div style={{ 
      maxWidth: '1400px', 
      margin: '0 auto', 
      padding: window.innerWidth < 768 ? '1rem' : '2rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: '0.75rem',
        padding: window.innerWidth < 768 ? '1.5rem 1rem' : '2rem',
        marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
      }}>
        <h1 style={{
          fontSize: window.innerWidth < 768 ? '1.5rem' : '2rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <GitCompare size={window.innerWidth < 768 ? 24 : 32} />
          ETF Comparison Tool
        </h1>
        <p style={{ 
          fontSize: window.innerWidth < 768 ? '0.875rem' : '1rem', 
          opacity: 0.9 
        }}>
          Compare up to 5 ETFs side-by-side: holdings, expenses, performance, and overlap
        </p>
      </div>

      {/* Input Section */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
        marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
      }}>
        <h3 style={{
          fontSize: window.innerWidth < 768 ? '1rem' : '1.125rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Enter ETF Symbols to Compare
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {etfSymbols.map((symbol, index) => (
            <div key={index}>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: etfSymbols.length > 1 ? '1fr auto' : '1fr',
                gap: '1rem',
                alignItems: 'center'
              }}>
                <div style={{ position: 'relative', width: '100%', overflow: 'visible' }}>
                  <input
                    type="text"
                    value={symbol}
                    onChange={(e) => updateETFSymbol(index, e.target.value)}
                    placeholder={`ETF ${index + 1} (e.g., SPY, QQQ, VOO)`}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: '#0f172a',
                      border: '1px solid #475569',
                      borderRadius: '0.5rem',
                      color: 'white',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                  
                  {/* ✅ AUTOCOMPLETE DROPDOWN */}
                  {activeSuggestionIndex === index && suggestions.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '0.5rem',
                      background: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '0.5rem',
                      maxHeight: '250px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                    }}>
                      {suggestions.map((etf, idx) => (
                        <div
                          key={idx}
                          onClick={() => selectSuggestion(index, etf)}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            borderBottom: idx < suggestions.length - 1 ? '1px solid #334155' : 'none',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ fontWeight: '600', color: '#10b981', fontSize: '0.875rem' }}>
                            {etf.symbol}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                            {etf.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {etfSymbols.length > 1 && (
                  <button
                    onClick={() => removeETFInput(index)}
                    style={{
                      width: '48px',
                      height: '48px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '0.5rem',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          display: 'flex',
          flexDirection: window.innerWidth < 640 ? 'column' : 'row',
          gap: window.innerWidth < 640 ? '0.75rem' : '1rem',
          marginTop: '1rem'
        }}>
          {etfSymbols.length < 5 && (
            <button
              onClick={addETFInput}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.5rem',
                color: '#6ee7b7',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                width: window.innerWidth < 640 ? '100%' : 'auto'
              }}
            >
              <Plus size={18} />
              Add Another ETF
            </button>
          )}

          <button
            onClick={compareETFs}
            disabled={loading || etfSymbols.filter(s => s.trim()).length < 2}
            style={{
              width: window.innerWidth < 640 ? '100%' : '170px',
              height: '48px',
              background: loading ? '#475569' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            {loading ? (
              <>
                <Loader size={18} className="spin" />
                Comparing...
              </>
            ) : (
              <>
                <GitCompare size={18} />
                Compare ETFs
              </>
            )}
          </button>
        </div>

        {/* ✅ QUICK POPULAR ETFs */}
        {!compareData && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Quick compare popular ETFs:
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  setEtfSymbols(['SPY', 'VOO', 'IVV']);
                }}
                style={{
                  padding: '0.375rem 0.75rem',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '0.375rem',
                  color: '#6ee7b7',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                S&P 500 ETFs
              </button>
              <button
                onClick={() => {
                  setEtfSymbols(['SPY', 'QQQ', 'IWM']);
                }}
                style={{
                  padding: '0.375rem 0.75rem',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '0.375rem',
                  color: '#6ee7b7',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                Major Indices
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            color: '#fca5a5',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={18} />
            {error}
          </div>
        )}
      </div>

      {/* Comparison Results */}
      {compareData && compareData.etfs && (
        <div>
          {/* Quick Stats Comparison */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
            marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
          }}>
            <h3 style={{
              fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem'
            }}>
              📊 Quick Stats Comparison
            </h3>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <ComparisonTable etfs={compareData.etfs} />
            </div>
          </div>

          {/* Expense Ratio Chart */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
            marginBottom: window.innerWidth < 768 ? '1rem' : '2rem'
          }}>
            <h3 style={{
              fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <DollarSign size={window.innerWidth < 768 ? 20 : 24} color="#10b981" />
              Expense Ratio Comparison
            </h3>
            <ExpenseChart etfs={compareData.etfs} colors={CHART_COLORS} />
          </div>

          {/* Holdings Overlap */}
          {compareData.overlap && compareData.overlap.length > 0 && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: window.innerWidth < 768 ? '1rem' : '1.5rem',
              marginBottom: window.innerWidth < 768 ? '1rem' : '2rem',
              overflowX: 'auto'
            }}>
              <h3 style={{
                fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
                fontWeight: '600',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Award size={window.innerWidth < 768 ? 20 : 24} color="#f59e0b" />
                Holdings Overlap Analysis
              </h3>
              <OverlapMatrix overlap={compareData.overlap} />
            </div>
          )}

          {/* Winner Analysis */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: window.innerWidth < 768 ? '1rem' : '1.5rem'
          }}>
            <h3 style={{
              fontSize: window.innerWidth < 768 ? '1.125rem' : '1.25rem',
              fontWeight: '600',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <TrendingUp size={window.innerWidth < 768 ? 20 : 24} color="#60a5fa" />
              Winner Analysis
            </h3>
            <WinnerAnalysis etfs={compareData.etfs} />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPONENT: Comparison Table
// ✅ FIXED: Access etf.expense_ratio directly (not etf.data.expense_ratio)
// ============================================
const ComparisonTable = ({ etfs }) => {
  return (
    <table style={{ 
      width: '100%', 
      borderCollapse: 'collapse',
      minWidth: window.innerWidth < 768 ? '600px' : 'auto'
    }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #334155' }}>
          <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Metric</th>
          {etfs.map((etf, idx) => (
            <th key={idx} style={{ ...tableHeaderStyle, textAlign: 'center' }}>
              {etf.symbol}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr style={{ borderBottom: '1px solid #334155' }}>
          <td style={{ ...tableCellStyle, fontWeight: '600' }}>Fund Name</td>
          {etfs.map((etf, idx) => (
            <td key={idx} style={{ ...tableCellStyle, textAlign: 'center' }}>
              {etf.name || etf.symbol}
            </td>
          ))}
        </tr>
        <tr style={{ borderBottom: '1px solid #334155' }}>
          <td style={{ ...tableCellStyle, fontWeight: '600' }}>Expense Ratio</td>
          {etfs.map((etf, idx) => (
            <td key={idx} style={{ ...tableCellStyle, textAlign: 'center', color: '#10b981' }}>
              {etf.expense_ratio || 'N/A'}
            </td>
          ))}
        </tr>
        <tr style={{ borderBottom: '1px solid #334155' }}>
          <td style={{ ...tableCellStyle, fontWeight: '600' }}>Dividend Yield</td>
          {etfs.map((etf, idx) => (
            <td key={idx} style={{ ...tableCellStyle, textAlign: 'center', color: '#8b5cf6' }}>
              {etf.dividend_yield || 'N/A'}
            </td>
          ))}
        </tr>
        <tr style={{ borderBottom: '1px solid #334155' }}>
          <td style={{ ...tableCellStyle, fontWeight: '600' }}>AUM</td>
          {etfs.map((etf, idx) => (
            <td key={idx} style={{ ...tableCellStyle, textAlign: 'center', color: '#f59e0b' }}>
              {etf.aum || 'N/A'}
            </td>
          ))}
        </tr>
        <tr style={{ borderBottom: '1px solid #334155' }}>
          <td style={{ ...tableCellStyle, fontWeight: '600' }}>Category</td>
          {etfs.map((etf, idx) => (
            <td key={idx} style={{ ...tableCellStyle, textAlign: 'center' }}>
              {etf.category || 'N/A'}
            </td>
          ))}
        </tr>
        <tr style={{ borderBottom: '1px solid #334155' }}>
          <td style={{ ...tableCellStyle, fontWeight: '600' }}>Issuer</td>
          {etfs.map((etf, idx) => (
            <td key={idx} style={{ ...tableCellStyle, textAlign: 'center' }}>
              {etf.issuer || 'N/A'}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};

// ============================================
// COMPONENT: Expense Chart
// ✅ FIXED: Access etf.expenseRatioRaw directly
// ============================================
const ExpenseChart = ({ etfs, colors }) => {
  const data = etfs.map((etf, idx) => ({
    name: etf.symbol,
    expense: etf.expenseRatioRaw || parseFloat(etf.expense_ratio) || 0,
    fill: colors[idx]
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis dataKey="name" stroke="#94a3b8" />
        <YAxis stroke="#94a3b8" tickFormatter={(value) => `${value}%`} />
        <Tooltip formatter={(value) => [`${value.toFixed(2)}%`, 'Expense Ratio']} />
        <Bar dataKey="expense">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ============================================
// COMPONENT: Overlap Matrix
// ============================================
const OverlapMatrix = ({ overlap }) => {
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {overlap.map((item, idx) => (
        <div key={idx} style={{
          padding: '1rem',
          background: 'rgba(15, 23, 42, 0.6)',
          borderRadius: '0.5rem',
          display: 'flex',
          flexDirection: window.innerWidth < 640 ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: window.innerWidth < 640 ? 'flex-start' : 'center',
          gap: '0.75rem'
        }}>
          <span style={{ color: '#cbd5e1', fontWeight: '600' }}>
            {item.etf1} vs {item.etf2}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: getOverlapColor(item.overlapPercentage) }}>
              {item.overlapPercentage}% Overlap
            </span>
            <OverlapBadge percentage={item.overlapPercentage} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// COMPONENT: Overlap Badge
// ============================================
const OverlapBadge = ({ percentage }) => {
  const value = parseFloat(percentage);
  let label, color;

  if (value < 20) {
    label = 'Low Overlap';
    color = '#10b981';
  } else if (value < 50) {
    label = 'Moderate Overlap';
    color = '#f59e0b';
  } else {
    label = 'High Overlap';
    color = '#ef4444';
  }

  return (
    <div style={{
      padding: '0.25rem 0.75rem',
      background: `${color}20`,
      border: `1px solid ${color}`,
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      color
    }}>
      {label}
    </div>
  );
};

// ============================================
// COMPONENT: Winner Analysis
// ✅ FIXED: Access etf.expenseRatioRaw, etf.dividend_yield, etf.aumRaw directly
// ============================================
const WinnerAnalysis = ({ etfs }) => {
  const lowestExpense = etfs.reduce((min, etf) => {
    const minExpense = min.expenseRatioRaw || Infinity;
    const currentExpense = etf.expenseRatioRaw || Infinity;
    return currentExpense < minExpense ? etf : min;
  });

  const highestYield = etfs.reduce((max, etf) => {
    const maxYield = parseFloat(max.dividend_yield) || 0;
    const currentYield = parseFloat(etf.dividend_yield) || 0;
    return currentYield > maxYield ? etf : max;
  });

  const largestAUM = etfs.reduce((max, etf) => {
    const maxAUM = max.aumRaw || 0;
    const currentAUM = etf.aumRaw || 0;
    return currentAUM > maxAUM ? etf : max;
  });

  const winners = [
    {
      category: 'Lowest Expense Ratio',
      winner: lowestExpense.symbol,
      value: lowestExpense.expense_ratio || 'N/A',
      icon: <DollarSign size={20} color="#10b981" />,
      color: '#10b981'
    },
    {
      category: 'Highest Dividend Yield',
      winner: highestYield.symbol,
      value: highestYield.dividend_yield || 'N/A',
      icon: <Percent size={20} color="#8b5cf6" />,
      color: '#8b5cf6'
    },
    {
      category: 'Largest AUM',
      winner: largestAUM.symbol,
      value: largestAUM.aum || 'N/A',
      icon: <Award size={20} color="#f59e0b" />,
      color: '#f59e0b'
    }
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
      {winners.map((winner, idx) => (
        <div key={idx} style={{
          padding: '1.25rem',
          background: `${winner.color}10`,
          border: `1px solid ${winner.color}44`,
          borderRadius: '0.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {winner.icon}
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>
              {winner.category}
            </span>
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: winner.color, marginBottom: '0.25rem' }}>
            {winner.winner}
          </div>
          <div style={{ fontSize: '1rem', color: '#cbd5e1' }}>
            {winner.value}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================
function getOverlapColor(percentage) {
  const value = parseFloat(percentage);
  if (value < 20) return '#10b981';
  if (value < 50) return '#f59e0b';
  return '#ef4444';
}

const tableHeaderStyle = {
  padding: '0.75rem',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#94a3b8',
  textTransform: 'uppercase'
};

const tableCellStyle = {
  padding: '0.75rem',
  fontSize: '0.875rem',
  color: '#cbd5e1'
};

export default ETFComparison;